import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    headers.set("organizationid", process.env.REACT_APP_ORG_ID);
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

export const apiSlice = createApi({
  reducerPath: "helior",
  baseQuery,
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: "/user/excelior-login",
        method: "POST",
        body: { ...credentials },
      }),
    }),
    register: builder.mutation({
      query: (data) => ({
        url: "/user/excelior-signup",
        method: "POST",
        body: { ...data },
      }),
    }),
    forgotPassword: builder.mutation({
      query: (data) => ({
        url: "/user/forgot-password",
        method: "POST",
        body: { ...data },
      }),
    }),
    resetPassword: builder.mutation({
      query: (data) => ({
        url: "/user/reset-password",
        method: "POST",
        body: { ...data },
      }),
    }),
    checkEmailExists: builder.mutation({
      query: (data) => ({
        url: "/user/user-exist",
        method: "POST",
        body: { ...data },
      }),
    }),
    getRoles: builder.query({
      query: () => "/user/get-userRole",
    }),
    activateAccount: builder.mutation({
      query: (data) => ({
        url: "/user/account-activation",
        method: "POST",
        body: { ...data },
      }),
    }),
    getProfile: builder.query({
      query: () => "/user/user-detail",
    }),
    getHeliorPlan: builder.query({
      query: () => `/crm/helior-plan`,
    }),
    getHeliorOne: builder.query({
      query: () => `/crm/helior-one/`,
    }),
    getHeOne: builder.query({
      query: ({ searchQuery, page = 1, limit = 10, sortBy, sortOrder }) => {
        let url = `/crm/collaborator/downline/helior-one`;
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
        });

        if (searchQuery && searchQuery.trim()) {
          params.append("search", searchQuery.trim());
        }

        return `${url}?${params.toString()}`;
      },
    }),
    getHePlan: builder.query({
      query: ({ searchQuery, page = 1, limit = 10, sortBy, sortOrder }) => {
        let url = `/crm/collaborator/downline/helior-plan`;
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
        });

        if (searchQuery && searchQuery.trim()) {
          params.append("search", searchQuery.trim());
        }

        return `${url}?${params.toString()}`;
      },
    }),
    getPaymentPeriodicity: builder.query({
      query: ({ planId, typeId }) =>
        `/crm/admin/payment-periodicity/${planId}?typeId=${typeId}`,
    }),
    purchaseHeliorPlan: builder.mutation({
      query: ({ data, planId }) => ({
        url: `/crm/helior-plan/purchase-plan/${planId}`,
        method: "POST",
        body: { ...data },
      }),
    }),
    payRecurringAmount: builder.mutation({
      query: ({ data, contractCode }) => ({
        url: `/crm/helior-plan/add-instalment/${contractCode}`,
        method: "POST",
        body: { ...data },
      }),
    }),
    getHeliorPlanDetails: builder.query({
      query: ({ planId }) => `/crm/helior-plan/${planId}`,
    }),
    getContractDetails: builder.query({
      query: ({ contractCode }) => `/crm/helior-plan/user/${contractCode}`,
    }),
    getPlanDetail: builder.mutation({
      query: (contractCode) => ({
        url: `/crm/helior-plan/user/${contractCode}`,
        method: "GET",
      }),
    }),
    getStaticValue: builder.query({
      query: () => `/crm/get-static-value`,
    }),
    getValue: builder.query({
      query: () => `/crm/admin/value`,
    }),
    purchaseHeliorOne: builder.mutation({
      query: ({ data, planId }) => ({
        url: `/crm/helior-one/purchase/${planId}`,
        method: "POST",
        body: { ...data },
      }),
    }),
    getSingleHOne: builder.query({
      query: ({ contractCode }) => `/crm/helior-one/${contractCode}`,
    }),
    getHOneDetail: builder.mutation({
      query: (contractCode) => ({
        url: `/crm/helior-one/${contractCode}`,
        method: "GET",
      }),
    }),
    sendReferralLink: builder.mutation({
      query: (data) => ({
        url: `/crm/collaborator/referral-link`,
        method: "POST",
        body: { ...data },
      }),
    }),
    getDownlineCollaborator: builder.query({
      query: () => `/crm/collaborator/down-line-collaborator`,
    }),
    getDownlineCollaborator2: builder.query({
      query: () => `/crm/collaborator/down-line-collaborator2`,
    }),
    getCollaborators: builder.query({
      query: ({ searchQuery, page = 1, limit = 10, sortBy, sortOrder }) => {
        let url = `/crm/collaborator/refer-collaborator`;
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
        });

        if (searchQuery && searchQuery.trim()) {
          params.append("search", searchQuery.trim());
        }

        return `${url}?${params.toString()}`;
      },
    }),
    getUserDetail: builder.query({
      query: ({ userId }) => `/crm/admin/user/${userId}`,
    }),
    updateUser: builder.mutation({
      query: ({ userId, data }) => ({
        url: `/crm/admin/user/${userId}`,
        method: "POST",
        body: { ...data },
      }),
    }),
    createUser: builder.mutation({
      query: ({ data }) => ({
        url: `/crm/bank/user`,
        method: "POST",
        body: { ...data },
      }),
    }),
    getUserBankDetails: builder.query({
      query: () => `/crm/bank/user`,
    }),
    getSingleCollaborator: builder.query({
      query: ({ userId }) => `/crm/admin/collaborator/${userId}`,
    }),
    getReferralCode: builder.query({
      query: () => `/crm/collaborator/referral-code`,
    }),
    getClients: builder.query({
      query: ({
        planId,
        searchQuery,
        page = 1,
        limit = 10,
        sortBy,
        sortOrder,
      }) => {
        let url = `/crm/collaborator/clients`;
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
        });

        if (searchQuery && searchQuery.trim()) {
          params.append("search", searchQuery.trim());
        }

        return `${url}?${params.toString()}`;
      },
    }),
    checkAffiliateCode: builder.mutation({
      query: (data) => ({
        url: `/user/referral-code-exist`,
        method: "POST",
        body: { ...data },
      }),
    }),
    getDiscount: builder.query({
      query: () => `/crm/discount/`,
    }),
    createDiscount: builder.mutation({
      query: (data) => ({
        url: `/crm/discount/`,
        method: "POST",
        body: { ...data },
      }),
    }),
    updateDiscount: builder.mutation({
      query: ({ data, discountId }) => ({
        url: `/crm/discount/${discountId}`,
        method: "POST",
        body: { ...data },
      }),
    }),
    deleteDiscount: builder.mutation({
      query: ({ id }) => ({
        url: `/crm/discount/${id}`,
        method: "DELETE",
      }),
    }),
    getQualification: builder.query({
      query: ({ collabId }) => `crm/collaborator/qualification/${collabId}`,
    }),
    getCommissions: builder.query({
      query: ({ lang, date }) => {
        return {
          url: `/crm/collaborator/commission?date=${date}`,
          headers: {
            "Accept-Language": lang,
          },
        };
      },
    }),
    getPoolbonus: builder.query({
      query: ({ lang }) => {
        return {
          url: `/crm/collaborator/pool-bonus`,
          headers: {
            "Accept-Language": lang,
          },
        };
      },
    }),
    requestForPayout: builder.mutation({
      query: (data) => ({
        url: `/crm/collaborator/request-payout`,
        method: "POST",
        body: { ...data },
      }),
    }),
    getRequestPayouts: builder.query({
      query: () => `/crm/collaborator/request-payout`,
    }),
    collabDashboad: builder.query({
      query: () => `/crm/collaborator/dashboard`,
    }),
    collabDashboadChart: builder.query({
      query: () => `/crm/collaborator/dash-board-charts`,
    }),

    /***
     * Updated Collaborator Screens Endpoints
     ***/
    // newCollabDashboard: builder.query({
    //   query: () => `/crm/collaborator/dash-board`,
    // }),
    newCollabDashboard: builder.query({
      query: ({ lang, date }) => {
        return {
          url: `/crm/collaborator/dash-board?month=${date.month}&year=${date.year}`,
          headers: {
            "Accept-Language": lang,
          },
        };
      },
    }),
     newExCollabDashboard: builder.query({
      query: ({ lang }) => {
        return {
          url: "/crm/collaborator/excelior-dashboard",
          headers: {
            "Accept-Language": lang,
          },
        };
      },
    }),
    careerData: builder.query({
      query: ({ lang, date }) => {
        return {
          url: `/crm/collaborator/career?date=${date}`,
          headers: {
            "Accept-Language": lang,
          },
        };
      },
    }),
    // getEwalletData: builder.query({
    //   query: ({ queryParams }) => {
    //     const params = new URLSearchParams();
    //     console.log(queryParams);
    //     if (queryParams.startDate) {
    //       params.append("startDate", queryParams.startDate);
    //     }

    //     if (queryParams.endDate) {
    //       params.append("endDate", queryParams.endDate);
    //     }

    //     if (queryParams.type) {
    //       params.append("type", queryParams.type);
    //     }

    //     if (queryParams.status) {
    //       params.append("status", queryParams.status);
    //     }

    //     if (queryParams.commissionType) {
    //       params.append("commissionType", queryParams.commissionType);
    //     }

    //     return `/crm/collaborator/e-wallet?${params.toString()}`;
    //   },
    // }),
    getEwalletData: builder.query({
      query: ({ commissionType, status, type, month, year }) => `/crm/collaborator/e-wallet?commissionType=${commissionType}&status=${status}&type=${type}&month=${month}&year=${year}`,
    }),
    getEwalletBalance: builder.query({
      query: () => "/crm/collaborator/get-ewallet-balance",
    }),
    updateIngotValue: builder.mutation({
      query: ({ contractCode, data }) => ({
        url: `/crm/helior-plan/update-ingot/${contractCode}`,
        method: "POST",
        body: { ...data },
      }),
    }),
    verifyOtp: builder.mutation({
      query: (data) => ({
        url: `/user/verify-otp`,
        method: "POST",
        body: { ...data },
      }),
    }),
    resendOtp: builder.mutation({
      query: (data) => ({
        url: `/user/resend-otp`,
        method: "POST",
        body: { ...data },
      }),
    }),
    getAccumulatedBars: builder.query({
      query: () => `/crm/collaborator/client/accumulated-gold`,
    }),
    /***
     * KYC
     ***/
    getKYCUpdateUrl: builder.mutation({
      query: () => {
        return {
          url: `/crm/kyc/get-kyc-update-url`,
          method: "GET",
        };
      },
    }),
    getkycurl: builder.mutation({
      query: ({ userType, language }) => {
        return {
          url: `/crm/kyc/get-kyc-url?entityType=${userType}&language=${language}`,
          method: "GET",
        };
      },
    }),
    getKYCStatus: builder.query({
      query: () => {
        return {
          url: `/crm/kyc/get-kyc-status`,
          method: "GET",
        };
      },
    }),
    getKYCData: builder.mutation({
      query: ({ language, userId }) => {
        return {
          url: `/crm/kyc/get-kyc-data?language=${language}&user_id=${userId}`,
          method: "GET",
        };
      },
    }),
    getTokenBalance: builder.query({
      query: () => `/excelior-users/token`,
    }),
    getNftBalance: builder.query({
      query: ({ lang }) => {
        return {
          url: `/excelior-users/nft`,
          headers: {
            'Accept-Language': lang,
          },
        };
      }
    }),
    changeLanguge: builder.mutation({
      query: ({ data }) => ({
        url: `/user/user-language`,
        method: "PUT",
        body: { ...data },
      }),
    }),
    getBankDetails: builder.query({
      query: ({ planId }) => {
        return {
          url: `crm/bank/get-bank-details/${planId}`,
          method: "GET",
        };
      },
    }),
    getProductionMonthList: builder.query({
      query: () => `/crm/collaborator/prod-listing`,
    }),
    getCommissionsData: builder.mutation({
      query: ({ lang, date }) => {
        return {
          url: `/crm/collaborator/commission?month=${date.month}&year=${date.year}`,
          method: "GET",
          headers: {
            "Accept-Language": lang,
          },
        };
      },
    }),
    getCareerData: builder.mutation({
      query: ({ lang, date }) => ({
        url: `/crm/collaborator/career?month=${date.month}&year=${date.year}`,
        method: "GET",
        headers: {
          "Accept-Language": lang,
        },
      }),
    }),
    generateEosKey: builder.query({
      query: () => `/user/generate-eos-key`,
    }),
    insertPublicKey: builder.mutation({
      query: (data) => ({
        url: `/user/update-public-key`,
        method: "POST",
        body: { ...data },
      }),
    }),
    isKeyAvailable: builder.query({
      query: () => `/user/is-key-available`,
    }),
    getUserNft: builder.query({
      query: () => "/excelior-users/nft",
    }),
    requestNewKey: builder.mutation({
      query: (data) => ({
        url: `excelior-users/lost-private-key`,
        method: "POST",
        body: { ...data },
      }),
    }),
    updateUserProfile: builder.mutation({
      query: (data) => ({
        url: `/crm/collaborator/update-user-details`,
        method: "PUT",
        body: { ...data },
      }),
    }),
    saveNftTransferRecord: builder.mutation({
      query: (data) => ({
        url: `/excelior-users/transfer-nft-metadata`,
        method: "POST",
        body: data,
      }),
    }),
    getLatestCurrencyRates: builder.query({
      query: () => `/crm/currency-rates/latest`,
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useCheckEmailExistsMutation,
  useGetRolesQuery,
  useActivateAccountMutation,
  useGetProfileQuery,
  useGetHeliorOneQuery,
  useGetHeliorPlanQuery,
  useGetPaymentPeriodicityQuery,
  usePurchaseHeliorPlanMutation,
  usePayRecurringAmountMutation,
  useGetHeliorPlanDetailsQuery,
  useGetContractDetailsQuery,
  useGetPlanDetailMutation,
  useGetStaticValueQuery,
  useGetValueQuery,
  usePurchaseHeliorOneMutation,
  useGetSingleHOneQuery,
  useGetHOneDetailMutation,
  useSendReferralLinkMutation,
  useGetDownlineCollaboratorQuery,
  useGetDownlineCollaborator2Query,
  useGetCollaboratorsQuery,
  useGetUserDetailQuery,
  useUpdateUserMutation,
  useCreateUserMutation,
  useGetSingleCollaboratorQuery,
  useGetReferralCodeQuery,
  useGetClientsQuery,
  useCheckAffiliateCodeMutation,
  useGetDiscountQuery,
  useCreateDiscountMutation,
  useUpdateDiscountMutation,
  useDeleteDiscountMutation,
  useGetQualificationQuery,
  useGetCommissionsQuery,
  useGetPoolbonusQuery,
  useRequestForPayoutMutation,
  useGetRequestPayoutsQuery,
  useCollabDashboadQuery,
  useCollabDashboadChartQuery,
  useGetAccumulatedBarsQuery,
  useGetHeOneQuery,
  useGetHePlanQuery,
  /***
   * Updated Collaborator Screens Endpoints
   ***/
  useNewCollabDashboardQuery,
  useNewExCollabDashboardQuery,
  useCareerDataQuery,
  useGetEwalletDataQuery,
  useGetEwalletBalanceQuery,
  useUpdateIngotValueMutation,
  /***
   * OTP System
   ***/
  useVerifyOtpMutation,
  useResendOtpMutation,
  /***
   * KYC
   ***/
  useGetKYCUpdateUrlMutation,
  useGetkycurlMutation,
  useGetKYCStatusQuery,
  useGetKYCDataMutation,
  useGetTokenBalanceQuery,
  useGetNftBalanceQuery,
  useGetUserBankDetailsQuery,
  useChangeLangugeMutation,
  useGenerateEosKeyQuery,
  useInsertPublicKeyMutation,
  useIsKeyAvailableQuery,
  useGetBankDetailsQuery,
  useGetProductionMonthListQuery,
  useGetCommissionsDataMutation,
  useGetCareerDataMutation,
  useGetUserNftQuery,
  useRequestNewKeyMutation,
  useUpdateUserProfileMutation,
  useSaveNftTransferRecordMutation,
  useGetLatestCurrencyRatesQuery,
} = apiSlice;
